import React from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'

const Footer = dynamic(() => import('@/components/home/footer'))
const KPHead = dynamic(() => import('@/components/head'))
const TopNavigation = dynamic(() => import('@/components/top-navigation'))

import { createResource, useResource } from '@/lib/i18n'
import styled from 'styled-components'
import { Animation, Reveal } from 'react-genie'

const resource = createResource({
  en: {
    contentTitle: 'Kredit Pintar Attention',
    description:
      'All impacts arising from lending and borrowing activities with Kredit Pintar, are borne by each party.',
    sections1:
      'The Information Technology-Based Loan Agreement (the “<b>Agreement</b>”) constitutes a civil agreement between the Lender and the Borrower. We only act as the organizer of Information Technology-Based Joint Funding Services (Layanan Pendanaan Bersama Berbasis Teknologi Informasi or “<b>LPBBTI</b>”) that facilitates the matching of Lenders and Borrowers through the electronic system provided by us.',
    sections2:
      'All risks arising from the funding activities under the Agreement, including but not limited to credit risk, default risk, payment delay risk, deterioration in funding quality, and any other risks of loss, shall be fully borne by the Lender in accordance with the provisions of the Agreement and applicable laws and regulations.',
    sections3:
      'We do not guarantee that the Lender will receive repayment of the principal amount of the funding, interest, returns, or any other economic benefits in full or on time.',
    sections4:
      'Before using the LPBBTI services, each Lender and Borrower (collectively, the “<b>Users</b>”) must read, understand, and agree to all applicable terms and conditions, including but not limited to the Terms of Use and Privacy Policy.',
    sections5:
      'Users who do not have sufficient knowledge and understanding of information technology-based funding activities are advised not to use the LPBBTI services provided by us.',
    sections6:
      'With the consent of each User, we may access, obtain, store, manage, and/or use the User’s personal data in accordance with our Privacy Policy and applicable laws and regulations.',
    sections7: 
      'The Borrower must carefully consider its financial capacity before applying for funding, including its ability to fulfil all payment obligations relating to principal, interest, fees, and other obligations under the Agreement.',
  },
  id: {
    contentTitle: 'Kredit Pintar Perhatian',
    description:
      'Segala dampak yang timbul dari kegiatan pinjam meminjam dengan Kredit Pintar, ditanggung oleh msaing-masing pihak.',
    sections1:
      'Perjanjian Pinjaman Uang Berbasis Teknologi Informasi (“<b>Perjanjian</b>”) merupakan kesepakatan perdata antara Pemberi Pinjaman dengan Penerima Pinjaman. Kami hanya bertindak sebagai penyelenggara Layanan Pendanaan Bersama Berbasis Teknologi Informasi (LPBBTI) yang mempertemukan Pemberi Dana dengan Penerima Dana melalui sistem elektronik yang disediakan oleh kami.',
    sections2:
      'Seluruh risiko yang timbul dari kegiatan pendanaan berdasarkan Perjanjian, termasuk namun tidak terbatas pada risiko kredit, risiko gagal bayar, risiko keterlambatan pembayaran, risiko penurunan kualitas pendanaan, maupun risiko kerugian lainnya, sepenuhnya menjadi tanggung jawab Pemberi Dana sesuai dengan ketentuan Perjanjian Pendanaan dan peraturan perundang-undangan yang berlaku.',
    sections3:
      'Kami tidak menjamin bahwa Pemberi Dana akan memperoleh pengembalian pokok pendanaan, bunga, imbal hasil, atau manfaat ekonomi lainnya secara penuh maupun tepat waktu.',
    sections4:
      'Sebelum menggunakan LPBBTI, masing-masing Pemberi Pinjaman dan Penerima Pinjaman (Pengguna) wajib membaca, memahami, dan menyetujui seluruh syarat dan ketentuan yang berlaku, termasuk namun tidak terbatas pada Ketentuan Penggunaan dan Kebijakan Privasi.',
    sections5:
      'Pengguna yang belum memiliki pengetahuan dan pemahaman yang memadai mengenai kegiatan pendanaan berbasis teknologi informasi disarankan untuk tidak menggunakan LPBBTI yang kami sediakan.',
    sections6:
      'Kami, dengan persetujuan dari masing-masing, Pengguna (baik Pemberi Pinjaman dan Penerima Pinjaman) dapat mengakses, memperoleh, menyimpan, mengelola dan/atau menggunakan data pribadi Pengguna sesuai dengan Kebijakan Privasi dan peraturan perundang-undangan yang berlaku.',
    sections7:
      'Penerima Pinjaman wajib mempertimbangkan kemampuan keuangannya secara cermat sebelum mengajukan pendanaan, termasuk kemampuan untuk memenuhi seluruh kewajiban pembayaran pokok, bunga, biaya dan kewajiban lainnya sesuai dengan Perjanjian.',
  },
})

const Attention = () => {
  const { t } = useResource(resource)
  return (
    <AttentionStyle>
      <Reveal animation={Animation.FadeIn}>
        <KPHead metaTitle="attention" />
        <Head>
          <meta name="description" content={t('description')} />
        </Head>
        <TopNavigation />
        <div className="attention">
          <h1 className="title">{t('contentTitle')}</h1>
          <div className="content">
            <div>
              <div dangerouslySetInnerHTML={{ __html: `<span>1. </span>${t('sections1')}` }} />
              <div>
                <span>2. </span>
                {t('sections2')}
              </div>
              <div>
                <span>3. </span>
                {t('sections3')}
              </div>
              <div dangerouslySetInnerHTML={{ __html: `<span>4. </span>${t('sections4')}` }} />                
              <div>
                <span>5. </span>
                {t('sections5')}
              </div>
              <div>
                <span>6. </span>
                {t('sections6')}
              </div>
              <div>
                <span>7. </span>
                {t('sections7')}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </Reveal>
    </AttentionStyle>
  )
}

export default Attention

const AttentionStyle = styled.div`
  width: 100%;
  min-height: 30vh;
  @media only screen and (max-width: 1000px) {
    .attention {
      margin: 0 auto;
      padding: 78px 24px 16px;
      .title {
        margin: 32px 0 24px;
        text-align: center;
        font-size: 24px;
        font-family: Roboto-Medium, Roboto;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        line-height: 28px;
      }
      .content {
        font-size: 16px;
        font-family: Roboto-Regular, Roboto;
        font-weight: 400;
        color: #757575;
        line-height: 24px;
        div {
          padding-bottom: 12px;
          span {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
          }
          b {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
            font-weight: 700;
          }
        }
      }
    }
  }
  @media only screen and (min-width: 1000px) {
    .attention {
      max-width: 1140px;
      margin: 0 auto;
      padding: 82px 0;
      border-bottom: 1px solid #eee;
      .title {
        margin: 80px 0 48px;
        text-align: center;
        font-size: 36px;
        font-family: Roboto-Medium, Roboto;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        line-height: 48px;
      }
      .content {
        display: flex;
        justify-content: space-between;
        font-size: 16px;
        font-family: Roboto-Regular, Roboto;
        font-weight: 400;
        color: #757575;
        line-height: 24px;
        div {
          padding-bottom: 20px;
          span {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
          }
          b {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
            font-weight: 700;
          }
        }
      }
    }
  }
`
